import {fork, take, call, cancel} from 'redux-saga/effects'
import {delay, eventChannel, Channel} from 'redux-saga'
import {AuthState} from '$src/auth/types'
import tryRefreshingTokens from './tryRefreshingTokens'
import decodeJwtToken from 'jwt-decode'
import {IStore} from '$src/bootstrap/Store'
import {getSnapshot, onAction, IStateTreeNode} from 'mobx-state-tree'
import {getMstStore} from '$src/bootstrap/rootSaga'
import {includes, startsWith} from 'lodash'

// If we have tokens in the state, this function will schedule a call
// to auth/refresh in order to keep our tokens fresh
function* reactToAuthStateChange(newState: AuthState): Generator {
  if (!newState.isLoggedIn) return

  // at this point, the goal is to keep the tokens fresh
  const tokenData: any = decodeJwtToken(newState.jwt)
  const tokenExpiresAt = new Date(tokenData.exp * 1000)

  const twentyMinutes = 20 * 60 * 1000
  if (tokenExpiresAt.getTime() < Date.now() + twentyMinutes)
    // if the token is going to expire within the next 20 minutes...
    return yield call(tryRefreshingTokens)
  else {
    // ... otherwise ...
    yield delay(tokenExpiresAt.getTime() - Date.now() - twentyMinutes) // ... wait until we reach that 20 minute threshold
    return yield call(tryRefreshingTokens)
  }
}

function channelOfActions(
  stateTree: IStateTreeNode,
  actionNames: string[],
  paths: string[] = [''],
): Channel<any> {
  return eventChannel(emitToChannel => {
    const listener = ({name, path, args}: {name: string; path: string; args: any}) => {
      if (includes(actionNames, name) && paths.some(p => startsWith(p, path))) {
        emitToChannel({name, path, args})
      }
    }

    const stopListeningToActions = onAction(stateTree, listener)

    return stopListeningToActions
  })
}
/**
 * This saga listens to all actions relate to auth tokens, and makes
 * sure that if we have new tokens, we correctly schedule to refresh
 * them.
 */
export default function* keepTokensValid(): Generator {
  const mstStore: IStore = yield getMstStore()
  const initialAuthState = getSnapshot(mstStore.auth)

  let lastTask = yield fork(reactToAuthStateChange, initialAuthState)
  const actionsChannel = yield channelOfActions(mstStore, ['__setState'], ['auth'])

  while (true) {
    // eslint-disable-line

    yield take(actionsChannel)
    yield cancel(lastTask)
    yield delay(5)
    const newState = mstStore.auth.getState()
    lastTask = yield fork(reactToAuthStateChange, newState)
  }
}



// WEBPACK FOOTER //
// ./src/auth/sagas/keepTokensValid.ts